import { useEffect, useRef } from 'react';

import { Timesheet } from './models';
import useTimesheets from './useTimesheets';

// AI vision extraction is async with no realtime channel, so a timesheet created or given an
// image is polled until it is scanned.
const SCAN_POLL_INTERVAL_MS = 5000; // check scan status every 5s
const MAX_SCAN_POLL_ATTEMPTS = 12; // stop after ~1 min so a stuck extraction never polls forever

interface UseTimesheetScanPollingParams {
  companyId: string | undefined;
  // Called with the scanned timesheet, or null when polling gave up.
  onSettled: (scannedTimesheet: Timesheet | null) => void;
  timesheetId: string | null;
}

export default function useTimesheetScanPolling({
  companyId,
  onSettled,
  timesheetId,
}: UseTimesheetScanPollingParams) {
  const { getTimesheetById } = useTimesheets();
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (!companyId || !timesheetId) {
      return;
    }

    let attempts = 0;
    const intervalId = setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_SCAN_POLL_ATTEMPTS) {
        clearInterval(intervalId);
        onSettledRef.current(null);

        return;
      }

      const timesheet = await getTimesheetById(companyId, timesheetId, {
        silent: true,
      });
      if (!timesheet?.aiProcessed) {
        return;
      }

      clearInterval(intervalId);
      onSettledRef.current(timesheet);
    }, SCAN_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [companyId, getTimesheetById, timesheetId]);
}
